import { Box, CircularProgress } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { gql, useMutation, useQuery } from "@outschool/ui-apollo";
import { MessageInput } from "@outschool/ui-components-classroom";
import React, { useMemo, useRef } from "react";

import {
  ThreadDetailQuery,
  ThreadDetailQueryVariables,
} from "../../generated/graphql";
import { useLearnerTeacherSupportEnded } from "../../lib/teacherSupport";
import { MARK_THREAD_AS_READ_MUTATION } from "../../mutations/MarkThreadAsRead";
import { THREAD_DETAIL_QUERY } from "../../queries/ThreadDetailQuery";
import ComposerUnavailableNotice from "../ComposerUnavailableNotice";
import { MESSAGE_TEACHER_POLL_INTERVAL_MS } from "./constants";
import ConversationTimeline from "./ConversationTimeline";
import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import ThreadViewLayout from "./ThreadViewLayout";
import {
  buildLearnerSender,
  getThreadPostTarget,
  getThreadPostTitle,
  isGeneralThread,
  toBubbleMessageData,
} from "./utils";

interface ThreadDetailViewProps {
  threadUid: string;
  postTitle?: string | null;
  onBack: () => void;
  onClose: () => void;
}

export default function ThreadDetailView({
  threadUid,
  postTitle: postTitleProp,
  onBack,
  onClose,
}: ThreadDetailViewProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadDetailView",
  );
  const { section, learner, teacherName } = useMessageTeacherDrawerContext();
  const teacherSupportEnded = useLearnerTeacherSupportEnded();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [markAsRead] = useMutation(MARK_THREAD_AS_READ_MUTATION, {
    onError: () => {},
    update(cache) {
      const threadCacheId = cache.identify({
        __typename: "Thread",
        uid: threadUid,
      });
      const prev = cache.readFragment<{ unreadCount: number }>({
        id: threadCacheId,
        fragment: gql`
          fragment ThreadUnread on Thread {
            unreadCount
          }
        `,
      });
      const previousUnread = prev?.unreadCount ?? 0;
      cache.modify({
        id: threadCacheId,
        fields: { unreadCount: () => 0 },
      });
      const sectionCacheId = cache.identify({
        __typename: "Section",
        uid: section.uid,
      });
      cache.modify({
        id: sectionCacheId,
        fields: {
          unreadThreadsCount(existing: number = 0) {
            return Math.max(0, existing - previousUnread);
          },
        },
      });
    },
  });

  const { data, loading, refetch } = useQuery<
    ThreadDetailQuery,
    ThreadDetailQueryVariables
  >(THREAD_DETAIL_QUERY, {
    variables: { threadUid },
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
    onCompleted: () => {
      markAsRead({ variables: { threadUid } });
    },
  });

  const thread = data?.thread;
  const messages = useMemo(
    () => (thread?.messages ?? []).map(toBubbleMessageData),
    [thread?.messages],
  );

  const hasPostTitle = (postTitleProp?.trim().length ?? 0) > 0;
  const threadPostTitle = getThreadPostTitle(thread);
  const isGeneral =
    !hasPostTitle && !threadPostTitle && isGeneralThread(thread);

  const title =
    postTitleProp ??
    threadPostTitle ??
    (isGeneral ? t("Ask {{teacherName}} anything", { teacherName }) : "");

  const postTarget = getThreadPostTarget(thread);

  return (
    <ThreadViewLayout
      postTitle={title}
      postSubtitle={undefined}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 12,
        }}
      >
        {loading && messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <ConversationTimeline
            messages={messages}
            conversationKey={threadUid}
            scrollContainerRef={scrollContainerRef}
          />
        )}
      </Box>

      {teacherSupportEnded ? (
        <ComposerUnavailableNotice
          message={t(
            "Teacher support for this class has ended, so messaging is no longer available.",
          )}
          sx={{ px: 16, py: 12 }}
        />
      ) : (
        <MessageInput
          placeholder={t("Message your teacher")}
          sectionUid={section.uid}
          learnerUid={learner.uid}
          threadUid={threadUid}
          {...postTarget}
          sender={buildLearnerSender(learner)}
          onMessageSent={() => refetch()}
          sx={{ borderColor: "divider" }}
        />
      )}
    </ThreadViewLayout>
  );
}
